import React, { useEffect, useMemo, useRef, useState } from 'react';
import AttachmentPreviewContent, {
  getOfficePreviewEmbedUrl,
} from 'components/Dashboard/components/AttachmentPreviewContent';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';

const OFFICE_PREVIEW_EXTENSIONS = ['xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx'];

const safeDecodeURIComponent = (value = '') => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getAttachmentName = (attachmentUrl = '') => {
  if (!attachmentUrl) return '';
  const parts = attachmentUrl.split('/');
  return safeDecodeURIComponent(parts[parts.length - 1] ?? '');
};

const getFileExtension = (filename = '', fileUrl = '') => {
  const cleanName = filename.split('?')[0].split('#')[0];
  if (cleanName.includes('.')) {
    return cleanName.split('.').pop().toLowerCase();
  }

  const decodedUrl = safeDecodeURIComponent(fileUrl).split('?')[0].split('#')[0];
  if (decodedUrl.includes('.')) {
    return decodedUrl.split('.').pop().toLowerCase();
  }

  return '';
};

const canPreviewExtension = (extension = '') => {
  return extension === 'pdf' || extension === 'docx' || OFFICE_PREVIEW_EXTENSIONS.includes(extension);
};

const getFileTypeLabel = (extension = '') => {
  const labels = {
    pdf: 'PDF',
    docx: 'Word',
    doc: 'Word',
    xls: 'Excel',
    xlsx: 'Excel',
    ppt: 'PowerPoint',
    pptx: 'PowerPoint',
  };
  return labels[extension] ?? (extension ? extension.toUpperCase() : 'File');
};

const downloadAttachment = async ({ url, filename, onComplete }) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download attachment. Status: ${response.status}`);
  }
  const blob = await response.blob();
  const blobURL = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobURL;
  anchor.style = 'display: none';
  if (filename?.length) anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(blobURL), 0);
  onComplete?.();
};

const AttachmentNavButton = ({ direction, disabled, onClick }) => (
  <button
    type='button'
    className={`general-button ${direction === 'prev' ? 'total-contributions-prev' : 'total-contributions-next'}`}
    disabled={disabled}
    onClick={onClick}
    aria-label={direction === 'prev' ? 'Previous attachment' : 'Next attachment'}
    style={{ width: '22px', height: '22px' }}
  >
    <span className={direction === 'prev' ? 'icon-arrow-back' : 'icon-arrow-next'} />
  </button>
);

const LobbyingMaterialAttachments = ({ attachments = [] }) => {
  const [activeAttachmentIndex, setActiveAttachmentIndex] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreMenuRef = useRef(null);

  useOutsideElement(moreMenuRef, () => setIsMoreOpen(false));

  const attachmentItems = useMemo(
    () =>
      (attachments ?? []).map((url) => {
        const name = getAttachmentName(url);
        const extension = getFileExtension(name, url);
        return {
          url,
          name,
          extension,
          typeLabel: getFileTypeLabel(extension),
          canPreview: canPreviewExtension(extension),
        };
      }),
    [attachments]
  );

  const attachmentCount = attachmentItems.length;
  const normalizedActiveIndex = attachmentCount > 0 ? Math.min(activeAttachmentIndex, attachmentCount - 1) : 0;
  const activeAttachment = attachmentCount > 0 ? attachmentItems[normalizedActiveIndex] : null;
  const hasMultipleAttachments = attachmentCount > 1;

  useEffect(() => {
    setPdfPage(1);
    setPdfPages(0);
  }, [activeAttachment?.url]);

  const isFirstAttachment = normalizedActiveIndex <= 0;
  const isLastAttachment = normalizedActiveIndex >= attachmentCount - 1;

  const switchAttachment = (direction = 1) => {
    if (!hasMultipleAttachments) return;
    setActiveAttachmentIndex((prev) => {
      const currentIndex = Math.min(prev, attachmentCount - 1);
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= attachmentCount) return currentIndex;
      return nextIndex;
    });
  };

  const handleDownload = async () => {
    if (!activeAttachment?.url || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadAttachment({
        url: activeAttachment.url,
        filename: activeAttachment.name,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const attachmentMetaLabel = useMemo(() => {
    if (!activeAttachment) return null;
    if (activeAttachment.extension === 'pdf') {
      if (pdfPages > 0) {
        return (
          <>
            {activeAttachment.typeLabel}
            <span className='paragraph-p5 px-1' aria-hidden='true'>
              •
            </span>
            {pdfPages} page{pdfPages === 1 ? '' : 's'}
          </>
        );
      }
      return activeAttachment.typeLabel;
    }
    if (activeAttachment.extension) {
      return activeAttachment.typeLabel;
    }
    return 'File';
  }, [activeAttachment, pdfPages]);

  const showPdfPageNavigation = activeAttachment?.extension === 'pdf' && pdfPages > 0;
  const showPreview = activeAttachment?.canPreview;

  if (!attachmentCount) return null;

  return (
    <div className='lobbying-material-attachments mb-5'>
      <h3 className='title-h5-m-bold mb-1'>Attachments</h3>
      <p className='text-main-blue-55 paragraph-p1 mb-4'>
        {attachmentCount} file{attachmentCount === 1 ? '' : 's'} attached
      </p>

      <div className='d-flex align-items-center justify-content-between gap-15 px-3 border-general specific-bottom-margin-12 flex-wrap border-grey-50 rounded'>
        <div className='d-flex justify-content-between flex-grow-1 align-items-center py-2 pl-2'>
          <p className='title-h4 font-weight-bold mb-0'>{activeAttachment?.name}</p>
          <p className='paragraph-p1 main-subtle-text mb-0'>{attachmentMetaLabel}</p>
        </div>
        {hasMultipleAttachments && (
          <div className='d-flex align-items-center border-left pl-3 specific-y-padding-10 h-100 total-contributions-buttons'>
            <AttachmentNavButton direction='prev' disabled={isFirstAttachment} onClick={() => switchAttachment(-1)} />
            <span className='paragraph-p1 main-subtle-text mx-2'>
              {normalizedActiveIndex + 1} of {attachmentCount} attachment{attachmentCount === 1 ? '' : 's'}
            </span>
            <AttachmentNavButton direction='next' disabled={isLastAttachment} onClick={() => switchAttachment(1)} />
          </div>
        )}
      </div>

      <div className={`${showPreview ? 'overflow-hidden' : ''} rounded`}>
        <div className='d-flex align-items-center justify-content-between gap-15 px-3 py-2 border-general specific-bottom-margin-12 flex-wrap border-grey-50 rounded bg-grey'>
          <p className='flex-grow-1 paragraph-p1 main-subtle-text mb-0 pl-2'>
            {showPreview
              ? `Showing preview of ${activeAttachment?.name}`
              : `Preview unavailable for ${activeAttachment?.name}`}
          </p>

          {showPreview && showPdfPageNavigation && (
            <div className='d-flex align-items-center border specific-top-padding-4 specific-bottom-padding-5 specific-x-padding-8 h-100 rounded-pill total-contributions-buttons bg-white'>
              <AttachmentNavButton
                direction='prev'
                disabled={pdfPage <= 1}
                onClick={() => setPdfPage((prev) => Math.max(1, prev - 1))}
              />
              <span className='nowrap-item paragraph-p3 main-subtle-text mx-3'>
                Page {pdfPage} of {pdfPages}
              </span>
              <AttachmentNavButton
                direction='next'
                disabled={pdfPage >= pdfPages}
                onClick={() => setPdfPage((prev) => Math.min(pdfPages, prev + 1))}
              />
            </div>
          )}

          <div className='d-flex align-items-center'>
            <button
              type='button'
              className='general-button px-4 specific-y-padding-5 border-general border-grey-50 rounded mr-2 hovered-button rounded-pill bg-main-white'
              disabled={isDownloading}
              onClick={handleDownload}
            >
              {isDownloading ? <i className='fas fa-spinner fa-spin' /> : 'Download'}
            </button>
            <div className='position-relative' ref={moreMenuRef}>
              <button
                type='button'
                className='general-button px-3 specific-y-padding-5 border-general border-grey-50 rounded d-flex align-items-center rounded-pill bg-main-white hovered-button'
                onClick={() => setIsMoreOpen((prev) => !prev)}
              >
                <span className={`dropdown-item-element dropdown-item-element-10 ml-2 ${isMoreOpen ? 'open' : ''}`}>
                  More{' '}
                </span>
              </button>
              {isMoreOpen && (
                <div
                  className='position-absolute top-45 right-0 box-shadow rounded bg-main-white py-2 z-100'
                  style={{ width: '150px' }}
                >
                  <button
                    type='button'
                    className='general-button list-action-button w-100 text-left px-3 py-2'
                    onClick={() => {
                      window.open(activeAttachment?.url, '_blank', 'noopener,noreferrer');
                      setIsMoreOpen(false);
                    }}
                  >
                    Open in new tab
                  </button>
                  {OFFICE_PREVIEW_EXTENSIONS.includes(activeAttachment?.extension) && (
                    <button
                      type='button'
                      className='general-button list-action-button w-100 text-left px-3 py-2'
                      onClick={() => {
                        window.open(getOfficePreviewEmbedUrl(activeAttachment.url), '_blank', 'noopener,noreferrer');
                        setIsMoreOpen(false);
                      }}
                    >
                      Open in Office viewer
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          {showPreview && (
            <AttachmentPreviewContent
              url={activeAttachment.url}
              filename={activeAttachment.name}
              pageNumber={pdfPage}
              onPageNumberChange={setPdfPage}
              onPdfLoadSuccess={({ numPages }) => {
                setPdfPages(numPages);
                setPdfPage((prev) => Math.min(prev, numPages || 1));
              }}
              showControls={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LobbyingMaterialAttachments;
